import { cn } from '@lib/cn';
import { absoluteTime } from '@lib/time';
import { Card, CardHeader, StatTile } from '@shared/ui';

import { fillHours, peakHour } from '../model/hours';
import { reasonLabel } from '../model/reasons';

import type { RequestFilters } from '../model/filters';
import type { RefusalSummary } from '../schemas/refusals';

/** Beyond a week of hours the strip scrolls rather than squeezing each bar to nothing. */
const SCROLL_AFTER_HOURS = 7 * 24;

/**
 * Counts by reason and by hour, over the same filters as the table. A range
 * with nothing in it shows zeros, never an absent panel. Plain bars, no chart
 * library (design 04, W2).
 */
export function RefusalsSummary({
    summary,
    window,
}: {
    summary: RefusalSummary;
    window: RequestFilters;
}) {
    const total = summary.byReason.reduce((sum, entry) => sum + entry.count, 0);
    const reasons = [...summary.byReason].sort((a, b) => b.count - a.count);
    const maxReason = Math.max(1, ...reasons.map((entry) => entry.count));
    const hours = fillHours(summary.byHour, window.from, window.to);
    const peak = peakHour(hours);
    const maxHour = Math.max(1, peak?.count ?? 0);

    return (
        <div className="mb-5 grid gap-4 lg:grid-cols-[220px_1fr]">
            <StatTile
                label="Refusals in range"
                value={total.toLocaleString()}
                hint={peak ? `Peak at ${absoluteTime(peak.hourStart)}` : 'No peak'}
            />
            <Card>
                <CardHeader title="By reason" />
                <div className="space-y-2 px-5 py-4" data-testid="unidentified-refusals-by-reason">
                    {reasons.length === 0 ? (
                        <p className="text-[13px] text-content-muted">0 refusals</p>
                    ) : (
                        reasons.map((entry) => (
                            <div key={entry.reason} className="flex items-center gap-3 text-[13px]">
                                <span className="w-44 shrink-0 truncate text-content-body">
                                    {reasonLabel(entry.reason)}
                                </span>
                                <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-inset">
                                    <span
                                        className="block h-full rounded-full bg-brand"
                                        style={{ width: `${(entry.count / maxReason) * 100}%` }}
                                    />
                                </span>
                                <span className="w-12 text-right font-semibold tabular-nums text-content-strong">
                                    {entry.count.toLocaleString()}
                                </span>
                            </div>
                        ))
                    )}
                </div>
                <CardHeader title="By hour" subtitle="UTC hours, shown in your time zone" />
                <div className="overflow-x-auto px-5 py-4">
                    <div
                        className={cn(
                            'flex h-20 items-end gap-px',
                            hours.length > SCROLL_AFTER_HOURS && 'w-max',
                        )}
                        data-testid="unidentified-refusals-by-hour"
                    >
                        {hours.map((hour) => (
                            <span
                                key={hour.hourStart}
                                title={`${absoluteTime(hour.hourStart)}: ${String(hour.count)}`}
                                className={cn(
                                    'min-w-[3px] flex-1 rounded-t-sm',
                                    hour.hourStart === peak?.hourStart ? 'bg-brand' : 'bg-line',
                                )}
                                style={{ height: `${Math.max(2, (hour.count / maxHour) * 100)}%` }}
                            />
                        ))}
                    </div>
                </div>
            </Card>
        </div>
    );
}
